import {
  Box,
  Menu,
  MenuItem,
  Checkbox,
  Typography,
  Divider,
} from "@mui/material";
import { useState } from "react";
import Button from "@mui/material/Button";
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

// Define the interface for a generic filter item for type safety
export interface FilterItem {
  id: string;
  label: string;
}

// Define the interface for the dropdown component's props
interface MultiSelectDropdownProps {
  categoryName: string;
  items: FilterItem[];
  selectedIds: string[];
  onSelectionChange: (newSelectedIds: string[]) => void;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  categoryName,
  items,
  selectedIds,
  onSelectionChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (id: string) => {
    const isSelected = selectedIds.includes(id);
    const newSelectedIds = isSelected
      ? selectedIds.filter((itemId: string) => itemId !== id)
      : [...selectedIds, id];
    onSelectionChange(newSelectedIds);
  };

  const selectedCount: number = selectedIds.length;
  const triggerLabel: string =
    selectedCount === 0 ? categoryName : `${categoryName} (${selectedCount})`;

  return (
    <Box sx={{ display: "inline-block" }}>
      {/* Dropdown Trigger Button */}
      <Button
        aria-controls={open ? "filter-menu-" + categoryName : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant={selectedCount > 0 ? "contained" : "outlined"}
        color="primary"
        startIcon={<FilterListIcon />}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          ...(selectedCount > 0 && {
            bgcolor: "#5c6bc0",
            "&:hover": { bgcolor: "#4e5baa" },
          }),
        }}
      >
        {triggerLabel}
      </Button>

      {/* Dropdown Menu Panel */}
      <Menu
        id={"filter-menu-" + categoryName}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
          // Max height and scroll logic is handled by MuiMenu in the Theme
        }}
        PaperProps={{
          sx: { borderRadius: 3, width: 280, mt: 1 },
        }}
      >
        {/* 'Select All' for the current category */}
        <MenuItem
          onClick={() =>
            onSelectionChange(
              selectedCount === items.length ? [] : items.map((item) => item.id)
            )
          }
          sx={{
            py: 1,
            bgcolor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
            fontWeight: "bold",
          }}
        >
          <Checkbox
            checked={selectedCount === items.length}
            indeterminate={selectedCount > 0 && selectedCount < items.length}
            size="small"
            sx={{ mr: 1 }}
            tabIndex={-1}
            disableRipple
          />
          <Typography variant="body2" fontWeight="bold">
            {selectedCount === items.length ? "Deselect All" : "Select All"}
          </Typography>
        </MenuItem>
        <Divider />

        {/* Individual Items List */}
        {items.map((item, index) => (
          <MenuItem
            key={item.id}
            onClick={() => handleToggle(item.id)}
            sx={{
              py: 0.5,
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Checkbox
              checked={selectedIds.includes(item.id)}
              size="small"
              sx={{ mr: 1 }}
              // Ensure the checkbox is decorative, letting the MenuItem handle the click
              tabIndex={-1}
              disableRipple={true}
            />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {item.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
